// Phone scale ("zoom") formula, extracted exactly from the applyPhoneScale
// closure inside the sidebar-width effect in App.tsx. Pure math only — the
// effect still owns publishing the result to the --phone-scale CSS var, and
// the divide-by-zoom pointer corrections stay inline at their App.tsx sites
// so the coordinate math remains visible where it happens.

/**
 * Compute the phone-device scale factor from the sidebar width and the
 * window inner height:
 *
 *   scale = max(0.6, min(sidebarWidth / 220, (innerHeight / 2 - 32) / 370))
 *
 * Why each piece exists:
 * - `sidebarWidth / 220`: 220 = SIDEBAR_MIN_PX in App.tsx, so scale is 1.0 at
 *   the minimum sidebar width and ~1.25 at the 275px max. Scaling WITH the
 *   sidebar width is INTENTIONAL — the docked phone grows as the user widens
 *   the sidebar. Do not remove it.
 * - `(innerHeight / 2 - 32) / 370`: the 50vh cap. The phone renders a fixed
 *   base layout (370px tall docked) magnified by CSS `zoom: var(--phone-scale)`
 *   on .phone-device, and its top edge must stay below the sidebar midpoint
 *   (height <= 50vh - 32px). This cap MUST live here in JS, baked into the
 *   scale itself — a CSS max-height on a zoomed box squashes the layout
 *   instead of shrinking it uniformly.
 * - `0.6` floor keeps the device legible on short viewports.
 *
 * No rounding or further clamping: the raw float is published to the CSS var
 * via String(scale).
 *
 * The four --phone-scale sync sites that must stay in agreement:
 *   1. The applyPhoneScale effect in App.tsx — the publisher (re-runs on
 *      window resize because innerHeight is an input, on sidebar drag, and
 *      via a ResizeObserver on aside.sidebar). It passes
 *      min(sidebarWidth state, the aside's RENDERED width) as sidebarWidth:
 *      state and reality can diverge, and the phone must never overflow the
 *      rail it docks in — TRAP(phone/scale-rendered-width).
 *   2. App.css `.phone-device` → `zoom: var(--phone-scale, 1)` — the consumer.
 *   3. App.css `.sidebar-has-docked-phone .sidebar-scroll-wrap` margin-bottom
 *      calc — reserves sidebar-nav space above the docked phone, tracking the
 *      same var (and mirroring the 370px/50vh-32px constants).
 *   4. Pointer math inside .phone-device in App.tsx — fixed-position elements
 *      inside the zoomed subtree must divide clientX/clientY by the effective
 *      zoom or they land offset from the cursor.
 * See docs/PHONE_ARCHITECTURE.md §9 Scale / zoom contract.
 */
export function computePhoneScale(sidebarWidth: number, innerHeight: number): number {
  const bySidebar = sidebarWidth / 220
  const byViewport = (innerHeight / 2 - 32) / 370
  return Math.max(0.6, Math.min(bySidebar, byViewport))
}
